import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import ComboFilter from "../components/ComboFilter";

interface AudienceRow {
  id: string;
  name: string;
  revenue_eur: number;
  prev_revenue_eur: number;
  max_date: string;
}

interface BudgetBreakdownRow {
  audience_id: string;
  budget_pool_id: string;
  budget_name: string;
  revenue_eur: number;
  prev_revenue_eur: number;
  conversion_cpx: number | null;
  target_roas: number | null;
  d1_ecpx_eur: number | null;
  prev_d1_ecpx_eur: number | null;
  d1_roas: number | null;
  prev_d1_roas: number | null;
}

const formatEUR = (value: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);

const formatEURCpx = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

function formatROAS(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatChange(current: number, prev: number): { label: string; up: boolean } | null {
  if (!prev || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct >= 0 };
}

function formatChangeCpx(current: number | null, prev: number | null): { label: string; up: boolean } | null {
  if (current == null || prev == null || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct <= 0 };
}

function formatChangeRoas(current: number | null, prev: number | null): { label: string; up: boolean } | null {
  if (current == null || prev == null || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  return { label: `${pct >= 0 ? "▲" : "▼"} ${pct >= 0 ? "+" : ""}${pct}%`, up: pct >= 0 };
}

function formatDateRange(maxDateStr: string): string {
  const end = new Date(maxDateStr);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`entity-list__chevron ${expanded ? "entity-list__chevron--expanded" : ""}`}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

interface ManagerRow {
  audience_id: string;
  account_manager: string | null;
  adops_manager: string | null;
  organization_name: string | null;
}

const MANAGER_SQL = `
  SELECT DISTINCT
    CAST(a.id AS STRING) AS audience_id,
    o.name AS organization_name,
    am.name AS account_manager,
    adops.name AS adops_manager
  FROM \`martin-test-datalab.postgres.audiences\` a
  JOIN \`martin-test-datalab.postgres.campaigns\` c ON c.audience_id = a.id
  JOIN \`martin-test-datalab.postgres.organizations\` o ON c.organization_id = o.id
  LEFT JOIN \`martin-test-datalab.postgres.users\` am ON o.account_manager_id = am.id
  LEFT JOIN \`martin-test-datalab.postgres.users\` adops ON o.adops_manager_id = adops.id
`;

const AUDIENCE_SQL = `
  WITH agg AS (
    SELECT
      CAST(audience_id AS STRING) AS id,
      ANY_VALUE(audience_name) AS name,
      DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY) AS max_date,
      SUM(CASE WHEN bid_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN revenue_eur ELSE 0 END) AS revenue_eur,
      SUM(CASE WHEN bid_date < DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN revenue_eur ELSE 0 END) AS prev_revenue_eur
    FROM \`remerge-dwh.ops.apro_campaign_level_cohorted_performance\`
    WHERE bid_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 15 DAY)
      AND bid_date <= DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY)
    GROUP BY audience_id
  )
  SELECT * FROM agg WHERE revenue_eur > 0 ORDER BY revenue_eur DESC
`;

const BUDGET_BREAKDOWN_SQL = `
  WITH agg AS (
    SELECT
      CAST(audience_id AS STRING) AS audience_id,
      CAST(budget_id AS STRING) AS budget_pool_id,
      ANY_VALUE(budget_name) AS budget_name,
      SUM(CASE WHEN bid_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN revenue_eur ELSE 0 END) AS revenue_eur,
      SUM(CASE WHEN bid_date < DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN revenue_eur ELSE 0 END) AS prev_revenue_eur,
      SUM(CASE WHEN bid_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN d1_ap_conversions ELSE 0 END) AS curr_conv,
      SUM(CASE WHEN bid_date < DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN d1_ap_conversions ELSE 0 END) AS prev_conv,
      SUM(CASE WHEN bid_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN d1_ap_customer_revenue_eur ELSE 0 END) AS curr_ap_rev,
      SUM(CASE WHEN bid_date < DATE_SUB(CURRENT_DATE(), INTERVAL 8 DAY) THEN d1_ap_customer_revenue_eur ELSE 0 END) AS prev_ap_rev
    FROM \`remerge-dwh.ops.apro_campaign_level_cohorted_performance\`
    WHERE bid_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 15 DAY)
      AND bid_date <= DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY)
      AND budget_name NOT LIKE '%(migrated)%'
    GROUP BY audience_id, budget_id
  )
  SELECT
    a.audience_id,
    a.budget_pool_id,
    a.budget_name,
    bp.conversion_cpx,
    bp.target_roas,
    a.revenue_eur,
    a.prev_revenue_eur,
    CASE WHEN a.curr_conv > 0 THEN a.revenue_eur / a.curr_conv END AS d1_ecpx_eur,
    CASE WHEN a.prev_conv > 0 THEN a.prev_revenue_eur / a.prev_conv END AS prev_d1_ecpx_eur,
    CASE WHEN a.revenue_eur > 0 THEN a.curr_ap_rev / a.revenue_eur END AS d1_roas,
    CASE WHEN a.prev_revenue_eur > 0 THEN a.prev_ap_rev / a.prev_revenue_eur END AS prev_d1_roas
  FROM agg a
  JOIN \`martin-test-datalab.postgres.budget_pools\` bp ON CAST(a.budget_pool_id AS INT64) = bp.id
  WHERE a.revenue_eur > 0
  ORDER BY a.audience_id, a.revenue_eur DESC
`;

interface AudienceMetrics {
  revenue_eur: number;
  prev_revenue_eur: number;
  d1_ecpx_eur: number | null;
  prev_d1_ecpx_eur: number | null;
  d1_roas: number | null;
  prev_d1_roas: number | null;
}

function aggregateMetrics(budgets: BudgetBreakdownRow[]): AudienceMetrics {
  let rev = 0,
    prevRev = 0;
  let impliedConv = 0,
    prevImpliedConv = 0;
  let impliedApRev = 0,
    prevImpliedApRev = 0;
  let hasConv = false,
    prevHasConv = false;
  let hasRoas = false,
    prevHasRoas = false;

  for (const b of budgets) {
    rev += b.revenue_eur;
    prevRev += b.prev_revenue_eur;
    if (b.d1_ecpx_eur != null && b.d1_ecpx_eur > 0) {
      impliedConv += b.revenue_eur / b.d1_ecpx_eur;
      hasConv = true;
    }
    if (b.prev_d1_ecpx_eur != null && b.prev_d1_ecpx_eur > 0) {
      prevImpliedConv += b.prev_revenue_eur / b.prev_d1_ecpx_eur;
      prevHasConv = true;
    }
    if (b.d1_roas != null) {
      impliedApRev += b.d1_roas * b.revenue_eur;
      hasRoas = true;
    }
    if (b.prev_d1_roas != null) {
      prevImpliedApRev += b.prev_d1_roas * b.prev_revenue_eur;
      prevHasRoas = true;
    }
  }

  return {
    revenue_eur: rev,
    prev_revenue_eur: prevRev,
    d1_ecpx_eur: hasConv && impliedConv > 0 ? rev / impliedConv : null,
    prev_d1_ecpx_eur: prevHasConv && prevImpliedConv > 0 ? prevRev / prevImpliedConv : null,
    d1_roas: hasRoas && rev > 0 ? impliedApRev / rev : null,
    prev_d1_roas: prevHasRoas && prevRev > 0 ? prevImpliedApRev / prevRev : null,
  };
}

export default function AudienceListPage() {
  const { isAuthenticated, requiresAuth } = useAuth();
  const [rows, setRows] = useState<AudienceRow[]>([]);
  const [budgetBreakdown, setBudgetBreakdown] = useState<Map<string, BudgetBreakdownRow[]>>(new Map());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [managerMap, setManagerMap] = useState<Map<string, ManagerRow>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [amFilter, setAmFilter] = useState("");
  const [adopsFilter, setAdopsFilter] = useState("");

  useEffect(() => {
    if (requiresAuth && !isAuthenticated) return;
    setIsLoading(true);
    setError(null);

    Promise.all([runBigQueryQuery(AUDIENCE_SQL), runBigQueryQuery(BUDGET_BREAKDOWN_SQL), runBigQueryQuery(MANAGER_SQL)])
      .then(([audienceResults, budgetResults, managerResults]) => {
        setRows(audienceResults as unknown as AudienceRow[]);
        const breakdown = new Map<string, BudgetBreakdownRow[]>();
        for (const row of budgetResults as unknown as BudgetBreakdownRow[]) {
          const existing = breakdown.get(row.audience_id) ?? [];
          existing.push(row);
          breakdown.set(row.audience_id, existing);
        }
        setBudgetBreakdown(breakdown);
        const mgrs = new Map<string, ManagerRow>();
        for (const row of managerResults as unknown as ManagerRow[]) {
          mgrs.set(row.audience_id, row);
        }
        setManagerMap(mgrs);
      })
      .catch(err => setError(String(err)))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated, requiresAuth]);

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) {
    return <div className="entity-list__loading">Loading…</div>;
  }

  if (error) {
    return (
      <div className="entity-list__error">
        <p>Something went wrong loading audiences. Please try refreshing the page.</p>
        <p className="entity-list__error-detail">{error}</p>
      </div>
    );
  }

  const dateRange = rows[0]?.max_date ? formatDateRange(rows[0].max_date) : null;
  const filterLower = filter.toLowerCase();

  // Build unique sorted AM/AdOps lists — only from audiences with revenue in the list
  const activeIds = new Set(rows.map(r => r.id));
  const activeManagers = [...managerMap.entries()].filter(([id]) => activeIds.has(id)).map(([, m]) => m);
  const amNames = [...new Set(activeManagers.map(m => m.account_manager).filter(Boolean) as string[])].sort();
  const adopsNames = [...new Set(activeManagers.map(m => m.adops_manager).filter(Boolean) as string[])].sort();

  const visibleRows = rows.filter(r => {
    if (filterLower) {
      const orgName = managerMap.get(r.id)?.organization_name ?? "";
      if (!r.name.toLowerCase().includes(filterLower) && !orgName.toLowerCase().includes(filterLower)) return false;
    }
    if (amFilter || adopsFilter) {
      const mgr = managerMap.get(r.id);
      if (amFilter && mgr?.account_manager !== amFilter) return false;
      if (adopsFilter && mgr?.adops_manager !== adopsFilter) return false;
    }
    return true;
  });

  // Summary stats from filtered rows
  const summary = (() => {
    let totalRev = 0,
      prevRev = 0,
      totalConv = 0,
      prevConv = 0,
      totalApRev = 0,
      prevApRev = 0;
    for (const row of visibleRows) {
      const budgets = budgetBreakdown.get(row.id) ?? [];
      const m = aggregateMetrics(budgets);
      totalRev += m.revenue_eur || row.revenue_eur;
      prevRev += m.prev_revenue_eur || row.prev_revenue_eur;
      if (m.d1_ecpx_eur != null && m.d1_ecpx_eur > 0) totalConv += (m.revenue_eur || row.revenue_eur) / m.d1_ecpx_eur;
      if (m.prev_d1_ecpx_eur != null && m.prev_d1_ecpx_eur > 0)
        prevConv += (m.prev_revenue_eur || row.prev_revenue_eur) / m.prev_d1_ecpx_eur;
      if (m.d1_roas != null) totalApRev += m.d1_roas * (m.revenue_eur || row.revenue_eur);
      if (m.prev_d1_roas != null) prevApRev += m.prev_d1_roas * (m.prev_revenue_eur || row.prev_revenue_eur);
    }
    const ecpx = totalConv > 0 ? totalRev / totalConv : null;
    const prevEcpx = prevConv > 0 ? prevRev / prevConv : null;
    const roas = totalRev > 0 ? totalApRev / totalRev : null;
    const prevRoas = prevRev > 0 ? prevApRev / prevRev : null;
    return { totalRev, prevRev, ecpx, prevEcpx, roas, prevRoas, count: visibleRows.length };
  })();
  const summaryRevChange = formatChange(summary.totalRev, summary.prevRev);
  const summaryEcpxChange = formatChangeCpx(summary.ecpx, summary.prevEcpx);
  const summaryRoasChange = formatChangeRoas(summary.roas, summary.prevRoas);

  return (
    <>
      {/* Summary KPIs — updates with filters */}
      <div className="audience-kpi-strip entity-list__summary-strip">
        <div className="audience-kpi-strip__item">
          <div className="entity-detail__kpi-label">Audiences</div>
          <div className="entity-detail__kpi-value">{summary.count}</div>
          {rows.length !== visibleRows.length && (
            <div className="entity-detail__kpi-change" style={{ color: "#8c8b7c" }}>
              of {rows.length}
            </div>
          )}
        </div>
        <div className="audience-kpi-strip__item">
          <div className="entity-detail__kpi-label">Revenue</div>
          <div className="entity-detail__kpi-value">{formatEUR(summary.totalRev)}</div>
          {summaryRevChange && (
            <div className="entity-detail__kpi-change">
              <span className={`entity-list__change entity-list__change--${summaryRevChange.up ? "up" : "down"}`}>
                {summaryRevChange.label} vs prev 7d
              </span>
            </div>
          )}
        </div>
        {summary.ecpx != null && (
          <div className="audience-kpi-strip__item">
            <div className="entity-detail__kpi-label">D1 AP eCPX</div>
            <div className="entity-detail__kpi-value">{formatEURCpx(summary.ecpx)}</div>
            {summaryEcpxChange && (
              <div className="entity-detail__kpi-change">
                <span className={`entity-list__change entity-list__change--${summaryEcpxChange.up ? "up" : "down"}`}>
                  {summaryEcpxChange.label}
                </span>
              </div>
            )}
          </div>
        )}
        {summary.roas != null && (
          <div className="audience-kpi-strip__item">
            <div className="entity-detail__kpi-label">D1 AP ROAS</div>
            <div className="entity-detail__kpi-value">{formatROAS(summary.roas)}</div>
            {summaryRoasChange && (
              <div className="entity-detail__kpi-change">
                <span className={`entity-list__change entity-list__change--${summaryRoasChange.up ? "up" : "down"}`}>
                  {summaryRoasChange.label}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="entity-list">
        <table className="entity-list__table">
          <caption className="entity-list__table-caption">
            <div className="entity-list__table-caption-inner">
              <div>
                <h1 className="entity-list__title">Audiences</h1>
                <p className="entity-list__subtitle">Sorted by revenue{dateRange ? ` — ${dateRange}` : ""}</p>
              </div>
              <div className="entity-list__filters">
                <input
                  className="entity-list__filter"
                  type="text"
                  placeholder="Filter…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
                <ComboFilter placeholder="Account Manager…" options={amNames} value={amFilter} onChange={setAmFilter} />
                <ComboFilter placeholder="AdOps…" options={adopsNames} value={adopsFilter} onChange={setAdopsFilter} />
              </div>
            </div>
          </caption>
          <thead>
            <tr>
              <th className="entity-list__col--expand"></th>
              <th>Audience</th>
              <th>Organization</th>
              <th className="entity-list__col--right">D1 AP eCPX</th>
              <th className="entity-list__col--right">D1 AP ROAS</th>
              <th className="entity-list__col--right">Revenue (EUR)</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(row => {
              const isExpanded = expandedIds.has(row.id);
              const budgets = budgetBreakdown.get(row.id) ?? [];
              const metrics = aggregateMetrics(budgets);
              const change = formatChange(
                metrics.revenue_eur || row.revenue_eur,
                metrics.prev_revenue_eur || row.prev_revenue_eur,
              );
              const cpxChange = formatChangeCpx(metrics.d1_ecpx_eur, metrics.prev_d1_ecpx_eur);
              const roasChange = formatChangeRoas(metrics.d1_roas, metrics.prev_d1_roas);
              return (
                <Fragment key={row.id}>
                  <tr className="entity-list__row">
                    <td className="entity-list__col--expand">
                      <button
                        type="button"
                        className="entity-list__expand-btn"
                        onClick={() => toggleExpand(row.id)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        <ChevronIcon expanded={isExpanded} />
                      </button>
                    </td>
                    <td className="entity-list__col--name" title={row.id}>
                      <Link to={`/audiences/${row.id}`} className="entity-list__link">
                        {row.name}
                      </Link>
                    </td>
                    <td className="entity-list__col--audiences">{managerMap.get(row.id)?.organization_name ?? ""}</td>
                    <td className="entity-list__col--right entity-list__col--revenue">
                      {metrics.d1_ecpx_eur != null ? formatEURCpx(metrics.d1_ecpx_eur) : "–"}
                      {cpxChange && (
                        <span className={`entity-list__change entity-list__change--${cpxChange.up ? "up" : "down"}`}>
                          {" "}
                          ({cpxChange.label})
                        </span>
                      )}
                    </td>
                    <td className="entity-list__col--right entity-list__col--revenue">
                      {metrics.d1_roas != null ? formatROAS(metrics.d1_roas) : "–"}
                      {roasChange && (
                        <span className={`entity-list__change entity-list__change--${roasChange.up ? "up" : "down"}`}>
                          {" "}
                          ({roasChange.label})
                        </span>
                      )}
                    </td>
                    <td className="entity-list__col--right entity-list__col--revenue">
                      {formatEUR(metrics.revenue_eur || row.revenue_eur)}
                      {change && (
                        <span className={`entity-list__change entity-list__change--${change.up ? "up" : "down"}`}>
                          {" "}
                          ({change.label})
                        </span>
                      )}
                    </td>
                  </tr>
                  {budgets.map(b => {
                    const bChange = formatChange(b.revenue_eur, b.prev_revenue_eur);
                    const hasCpxTarget = b.conversion_cpx != null && b.conversion_cpx > 0;
                    const hasRoasTarget = b.target_roas != null && b.target_roas > 0;
                    const cpxDimmed = hasRoasTarget && !hasCpxTarget;
                    const roasDimmed = hasCpxTarget && !hasRoasTarget;
                    const bCpxChange = formatChangeCpx(b.d1_ecpx_eur, b.prev_d1_ecpx_eur);
                    const bRoasChange = formatChangeRoas(b.d1_roas, b.prev_d1_roas);
                    return (
                      <tr
                        key={b.budget_pool_id}
                        className={`entity-list__budget-row${isExpanded ? " entity-list__budget-row--visible" : ""}`}
                      >
                        <td className="entity-list__col--expand" />
                        <td
                          className="entity-list__col--name entity-list__col--budget-name"
                          title={b.budget_pool_id}
                          colSpan={2}
                        >
                          <div className="entity-list__budget-cell">
                            <Link to={`/budgets/${b.budget_pool_id}`} className="entity-list__link">
                              {b.budget_name}
                            </Link>
                          </div>
                        </td>
                        <td
                          className={`entity-list__col--right entity-list__col--revenue${cpxDimmed ? " entity-list__col--dimmed" : ""}`}
                        >
                          <div className="entity-list__budget-cell">
                            {b.d1_ecpx_eur != null ? formatEURCpx(b.d1_ecpx_eur) : "–"}
                            {bCpxChange && (
                              <span
                                className={`entity-list__change entity-list__change--${bCpxChange.up ? "up" : "down"}`}
                              >
                                {" "}
                                ({bCpxChange.label})
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          className={`entity-list__col--right entity-list__col--revenue${roasDimmed ? " entity-list__col--dimmed" : ""}`}
                        >
                          <div className="entity-list__budget-cell">
                            {b.d1_roas != null ? formatROAS(b.d1_roas) : "–"}
                            {bRoasChange && (
                              <span
                                className={`entity-list__change entity-list__change--${bRoasChange.up ? "up" : "down"}`}
                              >
                                {" "}
                                ({bRoasChange.label})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="entity-list__col--right entity-list__col--revenue">
                          <div className="entity-list__budget-cell">
                            {formatEUR(b.revenue_eur)}
                            {bChange && (
                              <span
                                className={`entity-list__change entity-list__change--${bChange.up ? "up" : "down"}`}
                              >
                                {" "}
                                ({bChange.label})
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="entity-list__empty">
                  No audiences found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
